import { useMemo, useState } from 'react';

import { FiChevronLeft, FiSearch } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router';

import { MetaTags } from '@components';
import {
  useVideoDetailQuery,
  useVideosQuery,
  useVideoCategoriesQuery,
} from '@hooks';

import RelatedVideos from './RelatedVideos';
import VideoInfo from './VideoInfo';
import VideoPlayer from './VideoPlayer';
import * as S from './styles';

type VideoDetailParams = { videoId: string };

const VideoGallery = () => {
  const history = useHistory();
  const { videoId } = useParams<VideoDetailParams>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] =
    useState<number | undefined>();

  const { isLoading, data } = useVideosQuery(1, selectedCategory);
  const { data: categories } = useVideoCategoriesQuery();

  const firstVideoId = data?.videos[0]?.id;
  const selectedId = videoId ?? firstVideoId;

  const videoDetailQuery = useVideoDetailQuery(selectedId);

  const filteredVideos = useMemo(() => {
    if (!data?.videos) return [];
    if (!searchTerm.trim()) return data.videos;

    const term = searchTerm.toLowerCase();
    return data.videos.filter((video) =>
      video.title.toLowerCase().includes(term),
    );
  }, [data?.videos, searchTerm]);

  if (isLoading || !data?.videos.length) {
    return null;
  }

  const selectedVideo = videoDetailQuery.data;
  const total = data.videos.length;

  return (
    <S.Container>
      <MetaTags title="Galeria de Vídeos" />

      <S.PageHeader>
        <S.PageHeaderLeft>
          <S.BackButton type="button" onClick={() => history.goBack()}>
            <FiChevronLeft size="1.375rem" />
          </S.BackButton>
          <h1>Galeria de Vídeos</h1>
          <S.CountBadge>{total} disponíveis</S.CountBadge>
        </S.PageHeaderLeft>
        <S.SearchWrapper>
          <S.SearchIcon>
            <FiSearch size="1rem" />
          </S.SearchIcon>
          <S.SearchInput
            placeholder="Buscar vídeos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </S.SearchWrapper>
      </S.PageHeader>

      {categories && categories.length > 0 && (
        <S.CategoryFilters>
          <S.CategoryChip
            $active={selectedCategory === undefined}
            onClick={() => setSelectedCategory(undefined)}
          >
            Todos
          </S.CategoryChip>
          {categories.map((cat) => (
            <S.CategoryChip
              key={cat.id}
              $active={selectedCategory === cat.id}
              onClick={() =>
                setSelectedCategory(
                  selectedCategory === cat.id ? undefined : cat.id,
                )
              }
            >
              {cat.name}
            </S.CategoryChip>
          ))}
        </S.CategoryFilters>
      )}

      <S.Wrapper>
        {selectedVideo && (
          <>
            <VideoPlayer video={selectedVideo} />
            <VideoInfo video={selectedVideo} />
          </>
        )}
      </S.Wrapper>

      <RelatedVideos
        videos={filteredVideos}
        currentVideoId={selectedVideo?.id ?? 0}
      />
    </S.Container>
  );
};

export default VideoGallery;
